import React, { useEffect, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@material-ui/core'

import type { ScreenPlacement } from './LeadsPipelineTimeline'

export interface NameConfigDialogProps {
    open: boolean
    title: string
    confirmLabel?: string
    initialName: string
    submitting?: boolean
    /** Optional explanation above the name field. */
    message?: React.ReactNode
    /** Scratch pipeline create — pick Screening placement (templates already encode this). */
    askScreenPlacement?: boolean
    onClose: () => void
    onConfirm: (name: string, screenPlacement?: ScreenPlacement) => void
}

export const NameConfigDialog: React.FC<NameConfigDialogProps> = ({
    open,
    title,
    confirmLabel = 'Continue',
    initialName,
    submitting = false,
    message,
    askScreenPlacement = false,
    onClose,
    onConfirm,
}) => {
    const [name, setName] = useState(initialName)
    const [placement, setPlacement] = useState<ScreenPlacement>(
        'before_signing',
    )

    useEffect(() => {
        if (open) {
            setName(initialName)
            setPlacement('before_signing')
        }
    }, [open, initialName])

    const trimmed = name.trim()

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {message ? (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginBottom: 16 }}
                    >
                        {message}
                    </Typography>
                ) : null}
                <TextField
                    autoFocus
                    fullWidth
                    label="Configuration name"
                    variant="outlined"
                    margin="dense"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && trimmed && !submitting) {
                            onConfirm(
                                trimmed,
                                askScreenPlacement ? placement : undefined,
                            )
                        }
                    }}
                    placeholder="e.g. Qualify First"
                    helperText="You can rename this later in the editor."
                />
                {askScreenPlacement ? (
                    <FormControl
                        component="fieldset"
                        style={{ marginTop: 16, width: '100%' }}
                    >
                        <FormLabel component="legend">
                            When do you screen?
                        </FormLabel>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                            style={{ marginTop: 4, marginBottom: 8 }}
                        >
                            Screening and Qualification move together. Templates
                            already set this — only scratch needs a choice.
                        </Typography>
                        <RadioGroup
                            value={placement}
                            onChange={(_, v) =>
                                setPlacement(v as ScreenPlacement)
                            }
                        >
                            <FormControlLabel
                                value="before_signing"
                                control={<Radio color="primary" />}
                                label="Before signing — Application → Screening → Qualification → Lease"
                            />
                            <FormControlLabel
                                value="after_signing"
                                control={<Radio color="primary" />}
                                label="After signing — Application → Lease → Screening → Qualification"
                            />
                        </RadioGroup>
                    </FormControl>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!trimmed || submitting}
                    onClick={() =>
                        onConfirm(
                            trimmed,
                            askScreenPlacement ? placement : undefined,
                        )
                    }
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
